import React, { useEffect, useState } from "react";
import { fetchApi } from "../utils/fetchApi";
import { useBuilding } from "../context/buildingContext";
import { useRooms } from "../context/RoomsContext";
import TemperatureCard from "../components/TemperatureCard";
import { useTranslation } from "react-i18next";
import OutdoorCard from "../components/OutdoorCard";
import TemperatureUnitSelector from "../components/TemperatureUnitSelector";
import Modal from "../components/Modal";
import { displayFloorAndDoorByName } from '../utils/helpers';

export interface Room {
  name: string;
  temperature: number | string;
  measuredTemperature: number | string;
  humidity?: number | string;
  heatOrFreeze?: "heat" | "freeze";
  thermostatId: number;
  childLock: boolean;
  childLockCommand?: boolean;
  coolingDisabledDewPoint?: boolean;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { apartmentId, buildingId, apartmentName } = useBuilding();
  const { rooms, setRooms } = useRooms();
  const [outdoorData, setOutdoorData] = useState({ temperature: "N/A", humidity: "N/A" });
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [isQuickSettings, setIsQuickSettings] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState({country: "", city: "", address: ""});

  useEffect(() => {
    const fetchData = async () => {
      if (!apartmentId) {
        console.warn("No apartment ID available");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetchApi("get_apartment_data", {
          apartment_id: parseInt(apartmentId),
        });

        const thermostats = response.thermostats || [];
        const apartmentMode = response.operationMode;
        const fetchedRooms = thermostats.map((thermostat: any) => ({
          name: thermostat.name || "",
          temperature: thermostat.temperatureSetpointCommandDegC ? thermostat.temperatureSetpointCommandDegC : thermostat.temperatureSetpointDegC,
          humidity: thermostat.measuredHumPerc ?? "N/A",
          measuredTemperature: thermostat.measuredTempDegC ?? "N/A",
          heatOrFreeze: thermostat.active ? getHeatOrFreeze(apartmentMode) : undefined,
          thermostatId: thermostat.ID,
          coolingDisabledDewPoint: thermostat.coolingDisabledDewPoint,
          childLock:
            (thermostat.childLockCommand !== undefined && thermostat.childLockCommand !== null)
              ? thermostat.childLockCommand
              : thermostat.childLock ?? false,
        }));
        setRooms(fetchedRooms);
      } catch (error) {
        console.error("Error fetching thermostat data:", error);
        setError(t("dashboard.error.fetch"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apartmentId, t, setRooms]);

  useEffect(() => {
    const fetchBuilding = async () => {
      if (!buildingId) {
        console.warn("No building ID available");
        return;
      }

      try {
        const {data} = await fetchApi("get_building_data", {
          building_id: parseInt(buildingId),
        });
        setBuilding(data)
        setOutdoorData({
          temperature: data.externalTemperatureDegC !== -100.0 ? data.externalTemperatureDegC : "N/A",
          humidity: data.externalHumidityPerc !== -100.0 ? data.externalHumidityPerc : "N/A",
        });
      } catch (error) {
        console.error("Error fetching outdoor data:", error);
      }
    };

    fetchBuilding();
  }, [buildingId]);

  const openModal = (room: Room | null, quickSettings: boolean = false) => {
    setSelectedRoom(room);
    setIsQuickSettings(quickSettings);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedRoom(null);
    setIsQuickSettings(false);
  };

  const getHeatOrFreeze = (apartmentMode: number) => {
    if (apartmentMode === 0) return "heat";
    if (apartmentMode === 1) return "freeze";
    return undefined;
  };

  const handleTemperatureChange = async (newTemperature: number) => {
    try {
      if (isQuickSettings) {
        for (const room of rooms) {
          await fetchApi("update_thermostat_settings", {
            thermostat_id: room.thermostatId,
            temp_setpoint_degC: newTemperature,
          });
        }

        setRooms(
          rooms.map((room) => ({
            ...room,
            temperature: newTemperature,
          }))
        );
      } else if (selectedRoom) {
        const response = await fetchApi("update_thermostat_settings", {
          thermostat_id: selectedRoom.thermostatId,
          temp_setpoint_degC: newTemperature,
        });

        if (response.message === "Thermostat settings updated successfully.") {
          setRooms(
            rooms.map((room) =>
              room.thermostatId === selectedRoom.thermostatId
                ? {
                    ...room,
                    temperature: newTemperature,
                  }
                : room
            )
          );
        } else {
          console.error("Unexpected API response:", response);
        }
      }
    } catch (error) {
      console.error("Error updating thermostat settings:", error);
    } finally {
      closeModal();
    }
  };

  return (
    <div className="dashboard">
      {loading ? (
        <p>{t("loading")}</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          <div className='connectionStatus'>Kapcsolat rendben</div>
          <TemperatureUnitSelector />
          <h2>{`${building.country} ${building.city} ${building.address}, `}{displayFloorAndDoorByName(apartmentName)}</h2>
          <div className="outdoors">
            <OutdoorCard temperature={outdoorData.temperature} text={t("outdoorTemperature")} />
            <OutdoorCard temperature={outdoorData.humidity} text={t("outdoorHumidity")} isHumidity={true} />
          </div>
          <div className="rooms">
            {rooms.map((room) => (
              <TemperatureCard
                key={room.thermostatId}
                room={room.name}
                temperature={room.temperature}
                thermostatId={room.thermostatId}
                measuredTemperature={room.measuredTemperature}
                heatOrFreeze={room.heatOrFreeze}
                humidity={room.humidity}
                coolingDisabledDewPoint={room.coolingDisabledDewPoint}
                onClick={() => openModal(room)}
              />
            ))}
            <div className="temperatureCard quickSettingsCard" onClick={() => openModal(null, true)}>
              <h3>{t("quickSettings")}</h3>
            </div>
            {isModalOpen && (
              <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                room={
                  isQuickSettings
                    ? {
                      name: t("all"),
                      temperature: "N/A",
                      thermostatId: 0,
                      measuredTemperature: "N/A",
                      childLock: false
                    }
                    : selectedRoom!
                }
                onTemperatureChange={handleTemperatureChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
